/**
 * Centralized logging utility for Rendezvous
 * 
 * Features:
 * - Log levels (debug, info, warn, error)
 * - Environment-aware behavior (verbose in dev, minimal in prod)
 * - Automatic PII filtering
 * - Consistent formatting
 */

// Log levels with numeric values for comparison
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 100  // Special level to disable all logging
}

// Default configuration
interface LoggerConfig {
  minLevel: LogLevel;
  enableConsole: boolean;
  context?: string;
}

// Detect environment
const isProd = import.meta.env.PROD || process.env.NODE_ENV === 'production';

// Sensible defaults based on environment
const defaultConfig: LoggerConfig = {
  minLevel: isProd ? LogLevel.INFO : LogLevel.DEBUG,
  enableConsole: true
};

// Filter potentially sensitive information
function sanitizeLogData(data: any): any {
  if (!data) return data;
  if (typeof data !== 'object') return data;
  
  // Deep clone to avoid modifying the original
  const sanitized = Array.isArray(data) ? [...data] : {...data};
  
  // List of keys that might contain sensitive information
  const sensitiveKeys = [
    'password', 'token', 'secret', 'key', 'auth', 'credential', 
    'session', 'cookie', 'jwt', 'apiKey', 'credit_card', 'ssn', 'phone'
  ];
  
  // Recursively sanitize the object
  Object.keys(sanitized).forEach(key => {
    const lowerKey = key.toLowerCase();
    
    // Check if this key might contain sensitive info
    if (sensitiveKeys.some(sk => lowerKey.includes(sk))) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
      // Recursively sanitize nested objects
      sanitized[key] = sanitizeLogData(sanitized[key]);
    }
  });
  
  return sanitized;
}

/**
 * Create a logger instance with an optional context
 */
export function createLogger(context?: string): Logger {
  return new Logger({
    ...defaultConfig,
    context
  });
}

/**
 * Logger class that handles log filtering and formatting
 */
export class Logger {
  private config: LoggerConfig;
  
  constructor(config: LoggerConfig) {
    this.config = config;
  }
  
  // Shorthand methods for each log level
  public debug(message: string, ...data: any[]): void {
    this.log(LogLevel.DEBUG, message, ...data);
  }
  
  public info(message: string, ...data: any[]): void {
    this.log(LogLevel.INFO, message, ...data);
  }
  
  public warn(message: string, ...data: any[]): void {
    this.log(LogLevel.WARN, message, ...data);
  }
  
  public error(message: string, ...data: any[]): void {
    this.log(LogLevel.ERROR, message, ...data);
  }
  
  // Main logging method
  public log(level: LogLevel, message: string, ...data: any[]): void {
    // Skip if logging is disabled or below minimum level
    if (!this.config.enableConsole || level < this.config.minLevel) {
      return;
    }
    
    // Format the log message
    const timestamp = new Date().toISOString();
    const levelName = LogLevel[level];
    const contextPrefix = this.config.context ? `[${this.config.context}] ` : '';
    const formattedMessage = `${timestamp} ${levelName}: ${contextPrefix}${message}`;
    
    // Sanitize any data being logged
    const sanitizedData = data.map(item => sanitizeLogData(item));
    
    // Log to console with appropriate method
    switch (level) {
      case LogLevel.DEBUG:
        console.debug(formattedMessage, ...sanitizedData);
        break;
      case LogLevel.INFO:
        console.info(formattedMessage, ...sanitizedData);
        break;
      case LogLevel.WARN:
        console.warn(formattedMessage, ...sanitizedData);
        break;
      case LogLevel.ERROR:
        console.error(formattedMessage, ...sanitizedData);
        break;
    }
  }
  
  // Create a child logger with a specific context
  public createChild(childContext: string): Logger {
    const parentContext = this.config.context;
    const fullContext = parentContext ? `${parentContext}:${childContext}` : childContext;
    
    return new Logger({
      ...this.config,
      context: fullContext
    });
  }
}

// Export default logger for simple usage
export const logger = createLogger('Rendezvous');

// Export for convenience
export default logger; 